// features/intelligence/tabs/signal-matrix.js
// ═══════════════════════════════════════════════════════════
// SIGNAL MATRIX — quantitative record of what actually works
// for THIS user. Cross-references external conditions (from
// Dashboard data) with actual session outcomes (from
// Workstation). Builds over time. Minimum 14 sessions before
// meaningful patterns surface.
//
// Layer 1: empty-state with the "14 sessions needed" message,
// plus 2 sample patterns to show what the matrix will look
// like once data accumulates. Layer 2: pull from
// `signal_matrix` table once Workstation sessions exist.
// Layer 3: Claude generates the per-pattern Practitioner
// Lens explaining why each correlation matters.
// ═══════════════════════════════════════════════════════════

const SAMPLE_PATTERNS = [
  {
    condition: 'VIX > 18 + post-FOMC',
    metric: 'Squeeze setup outcomes',
    sample: '3 of 3 sessions',
    edge: '+12.4% avg',
    confidence: 'low (small sample)',
    tone: '#c8a96e',
  },
  {
    condition: 'FOMC days',
    metric: 'Average session return',
    sample: '8 of 11 sessions',
    edge: '+1.4% vs +0.8% non-FOMC',
    confidence: 'moderate',
    tone: '#7a9e82',
  },
];

export default function SignalMatrixTab() {
  return (
    <div className="di-scroll">
      <div className="di-section" style={{ marginBottom: 16 }}>
        <div className="di-sec-header">
          <div className="di-sec-label">Your Signal Matrix — Trading</div>
          <div className="di-sec-right">
            <div className="di-sec-time">11 sessions logged · 3 needed for first pattern</div>
          </div>
        </div>
        <div className="di-sec-body">
          <div style={{
            background: 'rgba(155, 143, 196, 0.04)',
            border: '1px solid rgba(155, 143, 196, 0.15)',
            borderRadius: 8, padding: '14px 16px', marginBottom: 14,
          }}>
            <div style={{
              fontFamily: 'DM Mono, monospace', fontSize: 8,
              letterSpacing: '0.14em', textTransform: 'uppercase',
              color: '#9b8fc4', marginBottom: 8,
            }}>
              How the matrix builds
            </div>
            <div style={{ fontSize: 13, color: '#c8c4e0', lineHeight: 1.7 }}>
              Each Workstation session you complete adds to this record. The matrix
              cross-references the day's external conditions (regime, vol, breadth,
              sector flow) with the outcome of your session. Patterns become
              meaningful around 14 sessions — that's when the system can tell you
              what actually works for <em style={{ color: '#5a9eb5', fontStyle: 'normal' }}>you</em>,
              not what should work in theory.
            </div>
          </div>

          <div className="di-sec-lbl">Patterns visible so far</div>

          {SAMPLE_PATTERNS.map(p => (
            <div key={p.condition} style={{
              background: '#111118', border: '1px solid #272736',
              borderLeft: `2px solid ${p.tone}`,
              borderRadius: 6, padding: '12px 16px', marginBottom: 8,
              display: 'grid', gridTemplateColumns: '1fr auto', gap: 14,
              alignItems: 'center',
            }}>
              <div>
                <div style={{
                  fontFamily: 'DM Mono, monospace', fontSize: 9,
                  color: '#888', textTransform: 'uppercase', letterSpacing: '0.1em',
                  marginBottom: 4,
                }}>
                  {p.condition}
                </div>
                <div style={{ fontSize: 13, color: '#c8c4e0', marginBottom: 4 }}>
                  {p.metric}
                </div>
                <div style={{ fontFamily: 'DM Mono, monospace', fontSize: 10, color: '#555' }}>
                  Sample: {p.sample} · Confidence: {p.confidence}
                </div>
              </div>
              <div style={{
                fontFamily: 'DM Mono, monospace', fontSize: 14,
                color: p.tone, fontWeight: 500, textAlign: 'right',
              }}>
                {p.edge}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="di-placeholder" style={{ height: 'auto', padding: '40px 20px' }}>
        <div className="di-eyebrow-tag">Coming next</div>
        <h2>Outcome correlation engine</h2>
        <p>
          Once you've logged 14 Workstation sessions, the matrix will surface up to
          12 cross-referenced patterns automatically — including conditions you
          didn't know to look for. The most personalized intelligence in Shift,
          built session by session, not bought.
        </p>
      </div>
    </div>
  );
}
